/**
 * Wire-boundary converters between Thrift enum ints and the lowercase strings
 * used in URLs/UI labels. Mirrors backend/services/enum_io.py.
 *
 * Uppercase enum names map to their lowercase form (API_CALL <-> api_call,
 * NODE_LOG <-> node_log). Round-trip is exact. Unknown values throw - no
 * silent fallback.
 */

import {
  WandInstancePool,
  WandLayerEventKind,
  WandLogChannel,
  WandRuntimeKind,
} from '@helinks/wand-api-thrift/wand_types';

function fromInt(enumObj: object, v: number, what: string): string {
  const name = (enumObj as Record<number, string>)[v];
  if (name === undefined) {
    throw new Error(`unknown ${what} enum value ${v}`);
  }
  return name.toLowerCase();
}

function toInt(enumObj: object, s: string, what: string): number {
  const upper = s.toUpperCase();
  const v = (enumObj as Record<string, number>)[upper];
  if (typeof v !== 'number') {
    throw new Error(`unknown ${what} string ${s}`);
  }
  return v;
}

export function runtimeKindToStr(v: WandRuntimeKind): string {
  return fromInt(WandRuntimeKind, v, 'WandRuntimeKind');
}

export function runtimeKindFromStr(s: string): WandRuntimeKind {
  return toInt(WandRuntimeKind, s, 'WandRuntimeKind') as WandRuntimeKind;
}

export function runtimeKindToStrOpt(v: WandRuntimeKind | undefined | null): string | undefined {
  return v == null ? undefined : runtimeKindToStr(v);
}

export function runtimeKindFromStrOpt(s: string | undefined | null): WandRuntimeKind | undefined {
  return s == null ? undefined : runtimeKindFromStr(s);
}

export function eventKindToStr(v: WandLayerEventKind): string {
  return fromInt(WandLayerEventKind, v, 'WandLayerEventKind');
}

export function eventKindFromStr(s: string): WandLayerEventKind {
  return toInt(WandLayerEventKind, s, 'WandLayerEventKind') as WandLayerEventKind;
}

export function logChannelToStr(v: WandLogChannel): string {
  return fromInt(WandLogChannel, v, 'WandLogChannel');
}

export function logChannelToStrOpt(v: WandLogChannel | undefined | null): string | undefined {
  return v == null ? undefined : logChannelToStr(v);
}

export function instancePoolToStr(v: WandInstancePool): string {
  return fromInt(WandInstancePool, v, 'WandInstancePool');
}

export function instancePoolFromStr(s: string): WandInstancePool {
  return toInt(WandInstancePool, s, 'WandInstancePool') as WandInstancePool;
}
